import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  Icon,
  NotProvided,
  Page,
  PageHeader,
  SidePanel,
} from '@evogard/ui';
import { careerRole, fromColumn, loadFailureMessage } from '@evogard/domain';
import type { AwardCategory, Period } from '@evogard/contract';
import { BackButton } from '../../components/BackButton.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { AwardsTotal, CategoryBlock, productsNotCounted } from './AwardBlocks.js';
import { ConsultantAwards } from './ConsultantAwards.js';
import { LotActions, LotStatus } from './LotInvoice.js';
import { AWARD_STALE_MS, awardPeriod, periodLabel, ReceiptMonthStrip } from './common.js';

/**
 * O INÍCIO do Comercial — as premiações do consultor no mês (28/10/2026,
 * pedido do dono). Desde o mesmo dia ele é também o que o Painel de Gestão era:
 * o painel saiu, e o Início assumiu o papel dele.
 *
 * Mostra a saudação, o acumulado do mês e as recorrências `1x` e `2x` lado a
 * lado, cada lote com o selo do pagamento no cabeçalho (`LotStatus`) e a ação
 * dele no pé (`LotActions`: anexar a nota, ou ver o pagamento). O lado
 * perdido de cada premiação está no relatório dela, a um clique.
 *
 * ## O Início de OUTRA pessoa — `?consultant=`
 *
 * Quem tem `sales.management:ver_qualquer` ou `:ver_carteiras` abre o Início
 * de um consultor pelo código dele — é para onde a tabela de todas as
 * carteiras leva. Mesmo desenho, com o nome no título e, para quem pode, o
 * botão de editar as premiações dele. A nota fiscal não aparece: ela é
 * documento de quem recebe. Sem a permissão, o parâmetro é ignorado e a pessoa
 * cai no próprio Início — quem protege de verdade é a API.
 *
 * A faixa de meses no topo escolhe a competência (28/10/2026). Ela vai na
 * busca (`?month=`), e é por isso que o link de cada premiação abre o
 * relatório no mesmo mês, e a volta dele cai aqui de novo no mês escolhido.
 *
 * Os blocos moram em `AwardBlocks.tsx`.
 */
export function SalesHomeScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const search = useSearch({ from: '/authenticated/sales/home' });
  const navigate = useNavigate();
  const period = (search.month as Period | undefined) ?? awardPeriod();
  const [editingAwards, setEditingAwards] = useState(false);

  const broad =
    can(context, 'sales.management', 'ver_qualquer') ||
    can(context, 'sales.management', 'ver_carteiras');
  const consultant = search.consultant;
  const own = consultant === undefined;

  const query = useQuery({
    queryKey: [
      'sales',
      'management',
      'panel',
      tenantId,
      consultant ?? 'me',
      period,
      'garantido',
    ],
    enabled: own || broad,
    queryFn: async () => {
      const r = await api.sales.managementPanel({
        query: { month: period, state: 'garantido', consultant },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o painel'));
      return r.body;
    },
    /* O mês já visto na faixa volta na hora, como no BI e no Extrato. */
    staleTime: AWARD_STALE_MS,
  });

  /*
   * A nota fiscal de cada lote — só no PRÓPRIO Início, e só para quem envia.
   * Mesma chave do cartão de nota de antes, então o envio atualiza tudo.
   */
  const sendsInvoice = own && can(context, 'management.payments', 'enviar_nf');
  const payments = useQuery({
    queryKey: ['management', 'payments', 'mine', period],
    enabled: sendsInvoice,
    queryFn: async () => {
      const r = await api.management.myPayments({ query: { month: period } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'a nota fiscal'));
      return r.body;
    },
  });
  const lotPayment = (category: string) =>
    payments.data?.estado === 'consultor'
      ? payments.data.items.find((p) => p.category === category && p.period === period)
      : undefined;

  /* Sem a permissão, o código do endereço não vale: o próprio Início. */
  if (!own && context != null && !broad) {
    return <Navigate to="/sales/home" search={{ month: search.month }} replace />;
  }

  const panel = query.data;
  const canEditAwards =
    !own &&
    (can(context, 'sales.management', 'editar_premiacoes') ||
      can(context, 'sales.commissions', 'configurar_premiacao'));
  const awardOwnerId = panel?.estado === 'medido' ? (panel.consultant.id ?? null) : null;
  const role = panel?.estado === 'medido' ? careerRoleOf(panel.award.categories) : null;
  const theirName =
    panel?.estado === 'medido' && panel.consultant.nome !== null
      ? panel.consultant.nome
      : `consultor ${consultant}`;

  return (
    <Page measure="cheia">
      <PageHeader
        back={
          own ? undefined : (
            <BackButton
              fallback={
                <Link
                  to="/sales/metrics"
                  search={{ state: 'garantido' }}
                  className={backLinkClasses()}
                >
                  <Icon nome="seta-esquerda" size={16} />
                  Todas as carteiras
                </Link>
              }
            />
          )
        }
        title={
          !own
            ? `Premiações de ${theirName}`
            : context != null
              ? `Olá, ${greetingName(context.user.nome)}`
              : 'Início'
        }
        actions={
          canEditAwards ? (
            <Button
              size="sm"
              variant="contorno"
              disabled={awardOwnerId === null}
              onClick={() => setEditingAwards(true)}
            >
              Adicionar e editar premiações
            </Button>
          ) : undefined
        }
      >
        {role !== null && (
          <Badge tone="informacao" className="mt-1">
            {role}
          </Badge>
        )}
        {/*
         * O botão fica desabilitado, e não some, quando o ERP conhece a pessoa e
         * a plataforma não: a premiação pertence ao vínculo da casa.
         */}
        {canEditAwards && panel?.estado === 'medido' && awardOwnerId === null
          ? 'Para editar as premiações, esta pessoa precisa de um vínculo ativo nesta base — vincule em Comercial › Consultores e times.'
          : undefined}
      </PageHeader>

      <ReceiptMonthStrip
        current={period}
        loading={query.isFetching}
        onChange={(month) =>
          void navigate({
            to: '/sales/home',
            /* O padrão sai do endereço: o Início sem busca é o mês de sempre. */
            search: {
              month: month === awardPeriod() ? undefined : month,
              consultant,
            },
            replace: true,
          })
        }
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {panel?.estado === 'sem_vinculo' && <Alert tone="alerta">{panel.message}</Alert>}

      {panel?.estado === 'medido' && (
        <>
          <AwardsTotal
            state="garantido"
            categories={panel.award.categories}
            label={`Acumulado de ${periodLabel(panel.award.month as Period)}`}
          />

          {panel.award.categories.length === 0 ? (
            <Card title="Bonificação por recorrência">
              <NotProvided>
                {own
                  ? 'Você não tem premiação de recorrência cadastrada nesta base.'
                  : 'Este consultor não tem premiação de recorrência cadastrada nesta base.'}
              </NotProvided>
            </Card>
          ) : (
            /*
             * Os lotes lado a lado, um cartão cada (28/10/2026, pedido do dono).
             * Abaixo de `lg` empilham: em meia largura de tablet a recusa
             * escrita por extenso quebra em várias linhas.
             */
            <div
              className={
                /*
                 * Dois lotes ficam lado a lado; um só fica com a mesma meia largura,
                 * centralizado — esticado na tela inteira, ele ficava vazio
                 * (28/10/2026, pedido do dono).
                 */
                panel.award.categories.length > 1
                  ? 'grid grid-cols-1 items-start gap-4 lg:grid-cols-2'
                  : 'grid grid-cols-1 items-start gap-4 lg:mx-auto lg:w-1/2'
              }
            >
              {panel.award.categories.map(withValue).map((category) => (
                <CategoryBlock
                  key={category.category}
                  category={category}
                  month={panel.award.month}
                  state="garantido"
                  consultant={consultant}
                  actions={
                    sendsInvoice ? (
                      <LotStatus payment={lotPayment(category.category)} />
                    ) : undefined
                  }
                  footer={
                    sendsInvoice ? (
                      <LotActions
                        payment={lotPayment(category.category)}
                        month={period}
                        canSend
                      />
                    ) : undefined
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {/*
       * O mesmo editor da ficha do consultor — não uma segunda cópia do
       * formulário —, num painel lateral largo. Salvar invalida
       * `['sales', 'management']`, então o Início atrás já mostra a premiação
       * nova ao fechar.
       */}
      {panel?.estado === 'medido' && awardOwnerId !== null && (
        <SidePanel
          open={editingAwards}
          onOpenChange={setEditingAwards}
          width="larga"
          title={`Premiações de ${theirName}`}
          descricao={`Código ${panel.consultant.erpVolunteerCode} nesta base — o mesmo cadastro de Comercial › Consultores e times, e o que se salva aqui vale lá.`}
        >
          {editingAwards && (
            <ConsultantAwards consultant={{ id: awardOwnerId }} frame="painel" />
          )}
        </SidePanel>
      )}
    </Page>
  );
}

/**
 * O lote só com as premiações que TÊM valor (28/10/2026, pedido do dono).
 *
 * Sai a apurada em R$ 0,00 — a de time de quem não tem liderado no mês, a
 * cadastrada que nada alcançou. A
 * RECUSADA fica: é ela que explica por que o total não aparece, e escondê-la
 * deixaria o total sumir sem motivo à vista. O total do lote é o do servidor,
 * e tirar uma linha de zero não muda a soma.
 *
 * A premiação por produto sem nenhum veículo consultado também fica
 * (03/11/2026): o zero dela é "não sei", e escondê-la fazia a Condição
 * Especial sumir do Início — ver `productsNotCounted`.
 */
function withValue(category: AwardCategory): AwardCategory {
  return {
    ...category,
    lines: category.lines.filter(
      (line) =>
        line.estado === 'recusado' ||
        productsNotCounted(line) ||
        (fromColumn(line.amount) ?? 0) !== 0,
    ),
  };
}

/**
 * O cargo do plano de carreira, abaixo da saudação (29/10/2026, pedido do
 * dono) — o mesmo que o cartão da "Antecipada Plano de Carreira" põe no
 * título: `careerRole` sobre a contagem dela no mês escolhido.
 *
 * Nulo quando a pessoa não tem essa premiação, quando ela foi recusada (não há
 * contagem) ou abaixo de 30 placas — e aí não há selo: a tela não diz cargo
 * que a contagem não alcançou.
 */
function careerRoleOf(categories: readonly AwardCategory[]): string | null {
  const counts = categories
    .flatMap((c) => c.lines)
    .filter((l) => l.family === 'antecipada' && l.plates !== null)
    .map((l) => l.plates as number);
  return counts.length === 0 ? null : careerRole(Math.max(...counts));
}

/**
 * O primeiro e o segundo nome — "Ana Beatriz", e não só "Ana". A partícula
 * vem junto e não conta como o segundo: "Maria da Silva Souza" vira "Maria da
 * Silva", porque cortar no "da" deixaria "Maria da".
 */
function greetingName(fullName: string): string {
  const words = fullName.trim().split(/\s+/);
  const particles = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
  const second = words.findIndex((w, i) => i > 0 && !particles.has(w.toLowerCase()));
  return second === -1 ? words.join(' ') : words.slice(0, second + 1).join(' ');
}
